import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { OVEN_IMAGE, Wordmark } from '@/components/brand/BrandAssets';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  // Get language from localStorage
  const language = localStorage.getItem('language') || localStorage.getItem('selectedLanguage') || 'lv';

  const translations = {
    loginTitle: {
      en: 'Login to Your Account',
      ru: 'Войти в аккаунт',
      lv: 'Pieslēgties savā kontā'
    },
    registerTitle: {
      en: 'Create an Account',
      ru: 'Создать аккаунт',
      lv: 'Izveidot kontu'
    },
    email: {
      en: 'Email',
      ru: 'Email',
      lv: 'E-pasts'
    },
    password: {
      en: 'Password',
      ru: 'Пароль',
      lv: 'Parole'
    },
    fullName: {
      en: 'Full Name',
      ru: 'Полное имя',
      lv: 'Pilns vārds'
    },
    phone: {
      en: 'Phone Number',
      ru: 'Номер телефона',
      lv: 'Tālruņa numurs'
    },
    loginButton: {
      en: 'Login',
      ru: 'Войти',
      lv: 'Piesakieties'
    },
    registerButton: {
      en: 'Register',
      ru: 'Зарегистрироваться',
      lv: 'Reģistrēties'
    },
    switchToRegister: {
      en: "Don't have an account? Register",
      ru: 'Нет аккаунта? Зарегистрируйтесь',
      lv: 'Nav konta? Reģistrējieties'
    },
    switchToLogin: {
      en: 'Already have an account? Login',
      ru: 'Уже есть аккаунт? Войдите',
      lv: 'Jau ir konts? Piesakieties'
    },
    forgotPassword: {
      en: 'Forgot Password?',
      ru: 'Забыли пароль?',
      lv: 'Aizmirsāt paroli?'
    },
    backToHome: {
      en: 'Back to Home',
      ru: 'Вернуться на главную',
      lv: 'Atpakaļ uz sākumu'
    },
    loginSuccess: {
      en: 'Login successful!',
      ru: 'Вход выполнен!',
      lv: 'Pierakstīšanās veiksmīga!'
    },
    registerSuccess: {
      en: 'Registration successful!',
      ru: 'Регистрация выполнена!',
      lv: 'Reģistrācija veiksmīga!'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login({
          email: formData.email,
          password: formData.password
        });

        if (result.success) {
          toast.success(translations.loginSuccess[language]);
          navigate('/');
        } else {
          toast.error(result.error);
        }
      } else {
        // Registration
        if (!formData.full_name || !formData.phone) {
          toast.error('Please fill in all required fields');
          setLoading(false);
          return;
        }

        const result = await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone
        });

        if (result.success) {
          toast.success(translations.registerSuccess[language]);
          navigate('/');
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rz-page rz-form min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block bg-[#1C1917]">
        <img src={OVEN_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0a09]/70 to-transparent" aria-hidden="true" />
        <div className="absolute bottom-12 left-12">
          <Wordmark size="footer" tone="light" />
        </div>
      </div>

      <div className="flex flex-col px-5 sm:px-10 lg:px-20 py-8 sm:py-10">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rz-link self-start inline-flex items-center gap-2 min-h-[44px] text-[0.7rem] font-medium tracking-[0.22em] uppercase text-[#78716C]"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {translations.backToHome[language]}
        </button>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-[440px] mx-auto lg:mx-0 py-10">
            <p className="rz-eyebrow m-0 mb-5">Roze di Napoli</p>
            <h1 className="font-display text-[2.4rem] sm:text-[3rem] leading-[1.02] text-[#1C1917] m-0 mb-10">
              {isLogin ? translations.loginTitle[language] : translations.registerTitle[language]}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="full_name">{translations.fullName[language]}</Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required={!isLogin}
                      placeholder="Anna Bērziņa"
                      data-testid="register-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{translations.phone[language]}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required={!isLogin}
                      placeholder="+371 20123456"
                      data-testid="register-phone-input"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{translations.email[language]}</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="your@email.com"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{translations.password[language]}</Label>
                  {isLogin && (
                    <Link to="/forgot-password" className="rz-link inline-flex items-center min-h-[44px] text-[0.7rem] tracking-[0.12em] uppercase text-[#78716C]" data-testid="forgot-password-link">
                      {translations.forgotPassword[language]}
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                className="rz-btn w-full !mt-10"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? '...' : (isLogin ? translations.loginButton[language] : translations.registerButton[language])}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-[#E7E5E4]">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="rz-link text-[0.95rem] text-[#57534E] min-h-[44px]"
                data-testid="toggle-login-register"
              >
                {isLogin ? translations.switchToRegister[language] : translations.switchToLogin[language]}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
